import { mountTimelineDock } from './timeline-stack.js';

// Timeline UNIFICATA v2 — port della semantica ASSE di map-timeline.js v1:
// slider unico −24h/+48h a RISOLUZIONE VARIABILE (5' nella finestra nowcast
// −90'/+30', 1h fuori). L'asse è la stessa lista di offset in MINUTI dal "now"
// arrotondato che costruisce buildAxis(full) in v1 (map-timeline.js:20-32).
//
// Adattamento dichiarato vs v1: in v1 lo slider è UNICO e si RESTRINGE alla
// finestra nowcast quando è attivo un layer radar/sat (shouldUseFullRange);
// nella v2 il nowcast ha già il suo dock dedicato (ui/nowcast-timeline.js),
// quindi questo dock è SEMPRE a range pieno −24h/+48h. Nessun'altra modifica
// alla semantica dell'asse.
//
// Contratto verso il resto della v2 (stations.js NON viene toccato):
// - PASSATO  → fetch /api/stations-archive-map.php?at=<iso> (param `at`
//   normalizzato al minuto, `cache_only=1` per il prefetch; 204 = cache miss),
//   righe trasformate nel formato atteso da stationDisplayRecord e hook
//   document.dispatchEvent 'wsv2:timeline-snapshot' {detail:{rows, T, at, validTime, count}}
// - FUTURO   → 'wsv2:timeline-future' {detail:{T}}   (il wiring collega WSLam/forecast)
// - T = ORA  → 'wsv2:timeline-live'   {detail:{T}}
// T è SEMPRE in millisecondi epoch (come NowcastTimeline.currentTime()).
//
// API: initUnifiedTimeline(map), setUnifiedEnabled(bool), currentTime().

// ---- Costanti asse (IDENTICHE a map-timeline.js v1) -------------------------
const RANGE_MIN = -24;          // ore
const DEFAULT_RANGE_MAX = 48;   // ore, esteso dalla copertura reale dei provider
const RANGE_MAX_CAP = 168;      // guardrail UI: una settimana, mai copertura inventata
const NOWCAST_PAST_MIN = 90;    // finestra densa: −90'
const NOWCAST_FWD_MIN = 30;     // finestra densa: +30'
const NOWCAST_STEP_MIN = 5;     // step denso 5'
const COARSE_STEP_MIN = 60;     // step orario fuori dalla finestra densa
const AUTOPLAY_MS = 900;        // v1 AUTOPLAY_MS
const SCRUB_DEBOUNCE_MS = 180;  // v1: fetch pesante ~180ms dopo lo stop del drag
const SNAPSHOT_CACHE_MAX = 64;  // v1: LRU cache snapshot

// buildAxis(full=true) v1: finestra densa 5' −90'..+30' + orario −1h..−24h e
// +1h..+48h. Lista di offset in MINUTI, ordinata (Set dedup: −90/−60/−30… vs orari).
function providerRangeMaxHours() {
    const coverage = window.WSWorkspace?.getCoverage?.();
    const baseMs = baseDate().getTime();
    const endMs = Number(coverage?.endMs);
    if (!Number.isFinite(endMs) || endMs <= baseMs) return DEFAULT_RANGE_MAX;
    return Math.min(RANGE_MAX_CAP, Math.max(DEFAULT_RANGE_MAX, Math.ceil((endMs - baseMs) / 3600000)));
}

function buildAxis() {
    const rangeMax = providerRangeMaxHours();
    state.rangeMaxHours = rangeMax;
    const mins = new Set();
    for (let m = -NOWCAST_PAST_MIN; m <= NOWCAST_FWD_MIN; m += NOWCAST_STEP_MIN) mins.add(m);
    for (let m = -COARSE_STEP_MIN; m >= RANGE_MIN * 60; m -= COARSE_STEP_MIN) mins.add(m);
    for (let m = COARSE_STEP_MIN; m <= rangeMax * 60; m += COARSE_STEP_MIN) mins.add(m);
    mins.add(0);
    return Array.from(mins).sort((a, b) => a - b);
}

const state = {
    map: null,
    enabled: false,
    axis: [],
    idx: 0,
    nowIndex: 0,
    offset: 0,          // offset corrente in MINUTI (= axis[idx])
    baseTime: null,     // "now" arrotondato al minuto (v1 roundedNow)
    requestSeq: 0,
    snapshotCache: new Map(),
    playTimer: 0,
    playing: false,
    prefetchTimer: 0,
    prefetchBaseKey: '',
    scrubTimer: 0,
    rangeMaxHours: DEFAULT_RANGE_MAX,
};
const pendingSnapshots = new Map();
let dock = null;

// ---- tempo ------------------------------------------------------------------
function roundedNow() {
    const d = new Date();
    d.setSeconds(0, 0);
    return d;
}
function baseDate() {
    if (!state.baseTime) state.baseTime = roundedNow();
    return new Date(state.baseTime.getTime());
}
function dateForOffset(offsetMin) {
    const d = baseDate();
    d.setMinutes(d.getMinutes() + Number(offsetMin || 0));
    return d;
}
function targetDate() {
    return dateForOffset(state.offset);
}
// Timestamp API v1 (map-timeline.js apiTimestamp): ISO UTC al minuto, 'Z' secco.
function apiTimestamp(date) {
    const copy = new Date(date.getTime());
    copy.setSeconds(0, 0);
    return copy.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// ---- indice/asse --------------------------------------------------------------
function clampIndex(value) {
    const n = Number.parseInt(value, 10);
    if (!Number.isFinite(n)) return state.nowIndex || 0;
    return Math.max(0, Math.min(Math.max(0, state.axis.length - 1), n));
}
function applyIndex(i) {
    state.idx = clampIndex(i);
    state.offset = state.axis[state.idx] ?? 0;
}
function indexForMinutes(min) {
    if (!state.axis.length) return 0;
    let best = 0, bestD = Infinity;
    for (let i = 0; i < state.axis.length; i++) {
        const d = Math.abs(state.axis[i] - min);
        if (d < bestD) { bestD = d; best = i; }
    }
    return best;
}
function rebuildAxis(keepNow) {
    const prevMin = state.offset;
    state.axis = buildAxis();
    state.nowIndex = state.axis.indexOf(0);
    if (state.nowIndex < 0) state.nowIndex = indexForMinutes(0);
    applyIndex(keepNow ? state.nowIndex : indexForMinutes(prevMin));
}

// ---- etichette (port formatOffsetLabel/formatTarget/formatEdge v1) ----------
const TARGET_FMT = new Intl.DateTimeFormat('it-IT', {
    weekday: 'short', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome',
});
function formatOffsetLabel(min) {
    const m = Number(min || 0);
    if (m === 0) return 'ORA';
    if (Math.abs(m) < 60) return (m > 0 ? `+${m}` : `${m}`) + ' min';
    const h = m / 60;
    const hh = Number.isInteger(h) ? h : h.toFixed(1);
    return (m > 0 ? `+${hh}` : `${hh}`) + 'h';
}
function labelHtml() {
    const hhmm = TARGET_FMT.format(targetDate());
    const off = formatOffsetLabel(state.offset);
    if (state.offset === 0) return `<b class="now">ORA</b> ${hhmm}`;
    if (state.offset < 0) return `<b class="past">${off}</b> ${hhmm}`;
    return `<b class="future">${off}</b> ${hhmm}`;
}

// ---- snapshot storico stazioni (port historicalStationSnapshot v1) ----------
// Righe archivio → formato atteso da stationDisplayRecord (lib/params.js):
// lat/lng numerici (dall'eventuale "coordinate"), alias campi archivio → nomi
// live usati da fieldForParam (pioggia←dayRain, raffica←rafficaMax). Gli altri
// campi (temperatura/umidita/vento/pressione/dewPoint/direzione/temp_mare/
// livello_idrometrico/network_category/is_stale…) arrivano GIÀ col nome giusto.
function normalizeSnapshotRow(row) {
    if (!row || typeof row !== 'object') return row;
    const s = { ...row };
    if (s.lng === undefined && s.lon !== undefined) s.lng = s.lon;
    if ((s.lat === undefined || s.lng === undefined) && (s.coordinate || s.coordinates)) {
        const parts = String(s.coordinate || s.coordinates).split(',').map(v => Number.parseFloat(v.trim()));
        if (Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
            s.lat = parts[0];
            s.lng = parts[1];
        }
    }
    if (s.pioggia === undefined) s.pioggia = s.dayRain ?? s.rain_day ?? null;
    if (s.raffica === undefined) s.raffica = s.rafficaMax ?? s.wind_gust ?? null;
    return s;
}
// v1 stationRowIsBuoyOnly: la boa ondametrica 2358 non è una stazione territorio.
function snapshotRowIsBuoyOnly(row) {
    const id = String(row?.id ?? row?.station_id ?? '').trim();
    const network = String(row?.network || row?.type || row?.tipo || row?.ente || '').trim().toUpperCase();
    return id === '2358' && network === 'ONDAMETRICA';
}
function transformSnapshotRows(rows) {
    return (Array.isArray(rows) ? rows : [])
        .map(normalizeSnapshotRow)
        .filter(row => row && !snapshotRowIsBuoyOnly(row));
}

async function historicalStationSnapshot(date, options = {}) {
    const at = apiTimestamp(date);
    if (state.snapshotCache.has(at)) return state.snapshotCache.get(at);
    const cacheOnly = !!options.cacheOnly;
    const pendingKey = `${at}:${cacheOnly ? 'cache' : 'fresh'}`;
    if (pendingSnapshots.has(pendingKey)) return pendingSnapshots.get(pendingKey);

    const promise = (async () => {
        let payload = null;
        try {
            const suffix = cacheOnly ? '&cache_only=1' : '';
            const response = await fetch(`/api/stations-archive-map.php?at=${encodeURIComponent(at)}${suffix}`, {
                headers: { Accept: 'application/json' },
                cache: 'force-cache',
            });
            if (response.status === 204) return null; // cache miss lato server (solo cache_only)
            if (response.ok) payload = await response.json();
        } catch (e) {
            console.warn('[wsv2] cache storico stazioni non disponibile:', e);
        }
        if (!payload && cacheOnly) return null;
        if (!payload) {
            if (!window.WSWeatherApi?.get) throw new Error('Weather Sicily API non disponibile');
            payload = await window.WSWeatherApi.get('/stations/archive-map', { at, limit: 2000 });
        }
        const snapshot = {
            at,
            valid_time: payload?.valid_time || at,
            count: Number(payload?.count) || 0,
            rows: transformSnapshotRows(payload?.stations),
        };
        state.snapshotCache.set(at, snapshot);
        if (state.snapshotCache.size > SNAPSHOT_CACHE_MAX) {
            state.snapshotCache.delete(state.snapshotCache.keys().next().value);
        }
        return snapshot;
    })();
    pendingSnapshots.set(pendingKey, promise);
    try {
        return await promise;
    } finally {
        pendingSnapshots.delete(pendingKey);
    }
}

// Prefetch cache-only (port prefetchHistoricalWindow v1: 1 richiesta alla volta).
function prefetchHistoricalWindow() {
    if (!state.enabled) return;
    const baseKey = apiTimestamp(baseDate());
    if (state.prefetchBaseKey === baseKey) return;
    state.prefetchBaseKey = baseKey;
    const offsets = [-60, -120, -180, -240, -360, -600, -720, -1080, -1440];
    let cursor = 0;
    let active = 0;
    const pump = () => {
        if (!state.enabled || state.prefetchBaseKey !== baseKey) return;
        while (active < 1 && cursor < offsets.length) {
            const offset = offsets[cursor++];
            active += 1;
            historicalStationSnapshot(dateForOffset(offset), { cacheOnly: true })
                .catch(() => {})
                .finally(() => { active -= 1; pump(); });
        }
    };
    window.setTimeout(pump, 450);
}
// Port prefetchStationSnapshots v1: vicini ±60' quando si è nel passato.
function prefetchNeighbors() {
    if (state.prefetchTimer) window.clearTimeout(state.prefetchTimer);
    state.prefetchTimer = window.setTimeout(() => {
        [-60, 60].forEach(deltaMin => {
            const offset = state.offset + deltaMin;
            if (offset >= RANGE_MIN * 60 && offset < 0) {
                historicalStationSnapshot(dateForOffset(offset), { cacheOnly: true }).catch(() => {});
            }
        });
    }, 80);
    if (state.offset <= 0) prefetchHistoricalWindow();
}

// ---- dispatch hook -----------------------------------------------------------
function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
}

function setNote(text) {
    const note = dock?.querySelector('.wsv2-ut-note');
    if (note) note.textContent = text || '';
}

function isCurrent(seq) {
    return state.enabled && seq === state.requestSeq;
}

// Applica l'istante corrente: passato=snapshot, futuro=forecast, 0=live.
async function applyTimeline() {
    if (!state.enabled) return;
    const seq = ++state.requestSeq;
    const T = targetDate().getTime();
    emit('wsv2:timeline-target', { T, mode: state.offset < 0 ? 'history' : state.offset > 0 ? 'future' : 'live' });
    if (state.offset === 0) {
        emit('wsv2:timeline-live', { T });
        setNote('Osservazioni live stazioni WS');
        return;
    }
    if (state.offset > 0) {
        emit('wsv2:timeline-future', { T });
        setNote(`Previsioni ${formatOffsetLabel(state.offset)}`);
        return;
    }
    // PASSATO: snapshot storico esatto
    setNote(`Carico storico WS ${apiTimestamp(targetDate())}…`);
    try {
        const snapshot = await historicalStationSnapshot(targetDate());
        if (!isCurrent(seq)) return;
        if (!snapshot) throw new Error('snapshot non disponibile');
        emit('wsv2:timeline-snapshot', {
            rows: snapshot.rows,
            T,
            at: snapshot.at,
            validTime: snapshot.valid_time,
            count: snapshot.count || snapshot.rows.length,
        });
        setNote(`Storico WS esatto ${snapshot.valid_time} · ${snapshot.count || snapshot.rows.length} stazioni`);
        prefetchNeighbors();
    } catch (e) {
        if (!isCurrent(seq)) return;
        setNote('Dati non disponibili per questo step');
        console.warn('[wsv2] timeline unificata: snapshot non disponibile', e);
    }
}

// ---- UI ----------------------------------------------------------------------
const UT_CSS = `
.wsv2-ut { z-index: 27; flex-wrap: wrap; max-width: min(620px, calc(100vw - 18px)); }
/* Se il dock nowcast è visibile, la timeline unificata sale di un piano. */
/* NB: escludi la barra cenere Etna (wsv2-nt--etna, sta in alto): senza il :not
   spingeva la timeline live a bottom:76px ogni volta che il layer Etna era acceso. */
body:has(.wsv2-nt:not(.wsv2-ut):not(.wsv2-nt--etna):not([hidden])) .wsv2-ut { bottom: 76px; }
.wsv2-ut-edge { display: grid; line-height: 1.05; text-align: center; color: var(--ws-fg-soft); font-size: 9.5px; font-weight: 800; }
.wsv2-ut-edge small { font-size: 7.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; opacity: .75; }
.wsv2-ut .wsv2-nt-label { min-width: 128px; }
.wsv2-ut-note { flex: 1 1 100%; text-align: center; color: var(--ws-fg-soft); font-size: 9.5px; font-weight: 650; min-height: 11px; }
/* Estremi live: riga a tutta larghezza in fondo al dock (max + min cliccabili). */
.wsv2-ut-extremes { flex: 1 1 100%; display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 4px 6px; margin-top: 1px; }
.wsv2-ut-extremes[hidden] { display: none; }
.wsv2-ut-extremes__lbl { color: var(--ws-fg-soft); font-size: 9px; font-weight: 850; text-transform: uppercase; letter-spacing: .07em; }
/* specificità (0,2,0) per battere '.wsv2-nt button' (0,1,1) che impone 38px+cerchio */
.wsv2-ut-extremes .wsv2-ut-ex { flex: 0 0 auto; width: auto; height: auto; display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border: 1px solid var(--ws-line); border-radius: 999px; background: var(--ws-chip); color: var(--ws-fg); cursor: pointer; font: inherit; white-space: nowrap; }
.wsv2-ut-extremes .wsv2-ut-ex:hover { border-color: var(--ws-accent); }
.wsv2-ut-ex i { font-style: normal; font-size: 10px; line-height: 1; }
.wsv2-ut-ex.is-max i { color: #e8543a; }
.wsv2-ut-ex.is-min i { color: #3a86e8; }
.wsv2-ut-ex b { font-weight: 850; font-size: 11px; font-variant-numeric: tabular-nums; }
.wsv2-ut-ex span { font-size: 10px; color: var(--ws-fg-soft); }
/* "Dato stazioni" in testa al dock (riga a tutta larghezza sopra la timeline). */
/* "Dato stazioni" = card DISTINTA (prima voce) sopra la timeline, che così
   resta pulita (solo play/slider/ora). Sfondo/bordo propri per staccarla. */
.wsv2-ut-data { flex: 1 1 100%; order: -1; display: flex; flex-wrap: wrap; align-items: center; gap: 5px 8px; padding: 6px 8px; margin: 0 0 7px; background: var(--ws-chip); border: 1px solid var(--ws-line); border-radius: 10px; }
.wsv2-ut-data[hidden] { display: none; }
.wsv2-ut-data__label { flex: 0 0 auto; color: var(--ws-fg-soft); font-size: 9px; font-weight: 850; text-transform: uppercase; letter-spacing: .07em; }
.wsv2-ut-data #selectParametro { flex: 1 1 140px; min-width: 0; padding: 6px 8px; }
.wsv2-ut-data .wsv2-active-box { flex: 1 1 100%; margin: 0; font-size: 10px; line-height: 1.2; color: var(--ws-fg-soft); }
.wsv2-ut-data .wsv2-active-box b { color: var(--ws-accent); font-weight: 850; }
.wsv2-ut-data .wsv2-active-box small { display: inline; margin-left: 6px; opacity: .8; }
@media (max-width: 640px) { .wsv2-ut-edge { display: none; } }
`;
let cssInjected = false;
function injectCss() {
    if (cssInjected) return;
    cssInjected = true;
    const style = document.createElement('style');
    style.textContent = UT_CSS;
    document.head.appendChild(style);
}

function ensureDock() {
    if (dock) return dock;
    injectCss();
    dock = document.createElement('div');
    dock.className = 'wsv2-panel wsv2-nt wsv2-ut';
    dock.hidden = true;
    dock.innerHTML = `
        <span class="wsv2-ut-edge wsv2-ut-edge--past">−24h<small>osservazioni</small></span>
        <button class="wsv2-nt-play" title="Avvia animazione">▶</button>
        <input type="range" min="0" max="0" value="0" aria-label="Timeline osservazioni e previsioni">
        <span class="wsv2-nt-label"></span>
        <button class="wsv2-nt-live" title="Torna a ora">●</button>
        <span class="wsv2-ut-edge wsv2-ut-edge--future">+48h<small>previsioni</small></span>
        <span class="wsv2-ut-note"></span>
        <div class="wsv2-ut-extremes" id="wsv2-ut-extremes" hidden></div>`;
    // In cima allo stack condiviso (order 0): quando c'è anche il radar/nowcast
    // si fondono in un unico riquadro con UNA sola ✕ (gestita da timeline-stack).
    mountTimelineDock(dock, { order: 0 });

    const range = dock.querySelector('input[type=range]');
    // Scrub v1: 'input' aggiorna SOLO l'etichetta (leggero), il carico dati è
    // debounced ~180ms dopo lo stop; 'change' flusha subito.
    range.addEventListener('input', () => {
        pause();
        applyIndex(range.value);
        renderScrubLabel();
        scheduleApply();
    });
    range.addEventListener('change', () => {
        applyIndex(range.value);
        flushApply();
    });
    dock.querySelector('.wsv2-nt-play').addEventListener('click', () => (state.playing ? pause() : play()));
    dock.querySelector('.wsv2-nt-live').addEventListener('click', () => {
        pause();
        applyIndex(state.nowIndex);
        renderScrubLabel(); // il cursore torna VISIVAMENTE su ORA (fix Giuseppe)
        flushApply();
    });
    return dock;
}

function scheduleApply() {
    if (state.scrubTimer) window.clearTimeout(state.scrubTimer);
    state.scrubTimer = window.setTimeout(() => {
        state.scrubTimer = 0;
        applyTimeline();
    }, SCRUB_DEBOUNCE_MS);
}
function flushApply() {
    if (state.scrubTimer) { window.clearTimeout(state.scrubTimer); state.scrubTimer = 0; }
    applyTimeline();
}

function renderScrubLabel() {
    if (!dock) return;
    const range = dock.querySelector('input[type=range]');
    const label = dock.querySelector('.wsv2-nt-label');
    range.value = String(state.idx);
    const pct = state.axis.length > 1 ? (state.nowIndex / (state.axis.length - 1)) * 100 : 0;
    range.style.setProperty('--now-pct', `${pct.toFixed(2)}%`);
    label.innerHTML = labelHtml();
    dock.querySelector('.wsv2-nt-live')?.classList.toggle('is-active', state.offset === 0);
}

function render() {
    if (!dock) return;
    const range = dock.querySelector('input[type=range]');
    range.min = '0';
    range.max = String(Math.max(0, state.axis.length - 1));
    const futureEdge = dock.querySelector('.wsv2-ut-edge--future');
    if (futureEdge) futureEdge.innerHTML = '+' + state.rangeMaxHours + 'h<small>previsioni</small>';
    renderScrubLabel();
}

function pause() {
    if (state.playTimer) window.clearInterval(state.playTimer);
    state.playTimer = 0;
    state.playing = false;
    dock?.querySelector('.wsv2-nt-play')?.replaceChildren('▶');
}
function play() {
    if (state.playing || state.axis.length < 2) return;
    state.playing = true;
    dock.querySelector('.wsv2-nt-play').replaceChildren('⏸');
    state.playTimer = window.setInterval(() => {
        if (state.idx >= state.axis.length - 1) { pause(); return; }
        applyIndex(state.idx + 1);
        renderScrubLabel();
        applyTimeline();
    }, AUTOPLAY_MS);
}

// ---- API pubblica -------------------------------------------------------------
export function initUnifiedTimeline(map) {
    state.map = map;
    ensureDock();
}

export function setUnifiedEnabled(enabled) {
    const on = !!enabled;
    if (on === state.enabled) return;
    ensureDock();
    if (on) {
        state.enabled = true;
        state.baseTime = roundedNow();
        state.prefetchBaseKey = '';
        rebuildAxis(true); // riparte da ORA
        dock.hidden = false;
        render();
        applyTimeline();
        prefetchHistoricalWindow();
    } else {
        pause();
        state.requestSeq += 1;
        if (state.scrubTimer) { window.clearTimeout(state.scrubTimer); state.scrubTimer = 0; }
        if (state.prefetchTimer) { window.clearTimeout(state.prefetchTimer); state.prefetchTimer = 0; }
        state.prefetchBaseKey = '';
        state.enabled = false;
        dock.hidden = true;
        setNote('');
        // Torna live: chi ascolta (stazioni/forecast) si riallinea al presente.
        emit('wsv2:timeline-live', { T: Date.now() });
    }
}

export function isUnifiedEnabled() {
    return state.enabled;
}

// Deep-link previsioni: porta la timeline direttamente nel futuro senza
// simulare interazioni sul range. L'offset viene agganciato allo step valido
// più vicino e attiva lo stesso flusso usato dallo scrub manuale.
export function setUnifiedOffsetMinutes(offsetMinutes) {
    if (!state.enabled) setUnifiedEnabled(true);
    rebuildAxis(false);
    applyIndex(indexForMinutes(Number(offsetMinutes) || 0));
    render();
    applyTimeline();
}

// Ricalcola soltanto l'asse visuale quando un provider pubblica una nuova
// copertura. Il limite destro segue i dati reali (es. WSLAM +72h) e non
// prolunga mai artificialmente l'ultimo frame disponibile.
export function refreshUnifiedCoverage() {
    if (!state.enabled) return;
    rebuildAxis(false);
    render();
}

export function getUnifiedRangeMinutes() {
    return Object.freeze({ min: RANGE_MIN * 60, max: state.rangeMaxHours * 60 });
}

// Istante corrente in ms epoch (come NowcastTimeline.currentTime()).
export function currentTime() {
    return targetDate().getTime();
}

// A forecast load can finish after a subsequent scrub. Only update the note
// for the selected future instant; never replace a live or historical note.
export function reportForecastAvailability(targetMs, available, failed = false) {
    if (!state.enabled || state.offset <= 0 || Math.abs(currentTime() - Number(targetMs)) > 1000) return;
    setNote(available ? `Previsioni ${formatOffsetLabel(state.offset)}`
        : failed ? 'Previsioni località temporaneamente non disponibili'
        : 'Previsioni località non disponibili per quest’ora');
}
